// v3 sends one policy per pet type and size, often with identical data (HP-9227): entries that
// show the same data merge, the no-size entry leads, and equal neighbouring sizes share a row.

import { PET_KINDS, type PetKind } from '@/src/lib/utils/petKind';
import { isPetSize, petSizeSortRank, type PetSize } from '@/src/lib/utils/petSize';

type Detail = string | number | null | undefined;

export type PetPolicyInput = {
  allowed?: boolean | null;
  petSize?: string | null;
  deposit?: Detail;
  oneTimeFee?: Detail;
  monthlyFee?: Detail;
  maxNumberAllowed?: Detail;
  maxWeightAllowed?: Detail;
  restriction?: string | null;
  comment?: string | null;
};

export type PetPolicyMap = Partial<Record<PetKind, Array<PetPolicyInput> | undefined>>;

export type PetRow = {
  key: string;
  kind: PetKind;
  sizes: Array<PetSize>;
  // null when the listing does not say; never render it as not allowed.
  allowed: boolean | null;
  deposit: string | null;
  oneTimeFee: string | null;
  monthlyFee: string | null;
  maxNumberAllowed: string | null;
  maxWeightAllowed: string | null;
  restriction: string | null;
  comment: string | null;
};

type Entry = Omit<PetRow, 'key' | 'kind' | 'sizes'> & { size: PetSize | null; signature: string };

const asText = (value: Detail): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const TYPE_ONLY_COMMENTS: Partial<Record<PetKind, Array<string>>> = {
  dog: ['dog', 'dogs'],
  cat: ['cat', 'cats'],
};

function toEntry(kind: PetKind, policy: PetPolicyInput): Entry {
  const rawSize = policy.petSize?.toLowerCase() ?? '';
  const size = isPetSize(rawSize) ? rawSize : null;
  const comment = asText(policy.comment);
  const fields = {
    allowed: typeof policy.allowed === 'boolean' ? policy.allowed : null,
    deposit: asText(policy.deposit),
    oneTimeFee: asText(policy.oneTimeFee),
    monthlyFee: asText(policy.monthlyFee),
    maxNumberAllowed: asText(policy.maxNumberAllowed),
    maxWeightAllowed: asText(policy.maxWeightAllowed),
    restriction: asText(policy.restriction),
    comment: comment && TYPE_ONLY_COMMENTS[kind]?.includes(comment.toLowerCase()) ? null : comment,
  };
  return { ...fields, size, signature: JSON.stringify(fields) };
}

type EntryField = Exclude<keyof Entry, 'size' | 'signature'>;

// Every field of an entry, so tsc fails here when PetRow gains one.
const ENTRY_FIELDS = Object.keys({
  allowed: true,
  deposit: true,
  oneTimeFee: true,
  monthlyFee: true,
  maxNumberAllowed: true,
  maxWeightAllowed: true,
  restriction: true,
  comment: true,
} satisfies Record<EntryField, true>) as Array<EntryField>;

// A fee-less "all" policy next to a $20 no-size one is one policy, not two rows (HP-9297).
const covers = (wider: Entry, narrower: Entry): boolean =>
  wider.signature !== narrower.signature &&
  ENTRY_FIELDS.every((field) => narrower[field] === null || narrower[field] === wider[field]);

function rowsForKind(kind: PetKind, policies: Array<PetPolicyInput>): Array<PetRow> {
  const entries = policies.map((policy) => toEntry(kind, policy));
  const generic = entries.filter((entry) => entry.size === null);
  const genericSignatures = new Set(generic.map((entry) => entry.signature));
  // Every distinct sized policy survives (two different "small" policies both show), sorted by size.
  const sized = entries
    .filter(
      (entry): entry is Entry & { size: PetSize } => entry.size !== null && !genericSignatures.has(entry.signature),
    )
    .filter(
      (entry, index, all) =>
        all.findIndex((other) => other.size === entry.size && other.signature === entry.signature) === index,
    )
    .sort((a, b) => petSizeSortRank(a.size) - petSizeSortRank(b.size));

  const toRow = (entry: Entry, sizes: Array<PetSize>): PetRow => {
    const { size: _size, signature: _signature, ...fields } = entry;
    return { key: `${kind}-${sizes.join('-') || 'any'}`, kind, sizes, ...fields };
  };

  const uniqueGeneric = generic.filter(
    (entry, index) =>
      generic.findIndex((other) => other.signature === entry.signature) === index &&
      !generic.some((other) => covers(other, entry)),
  );
  if (
    uniqueGeneric.length === 0 &&
    sized.length > 1 &&
    sized.every((entry) => entry.signature === sized[0]!.signature)
  ) {
    return [toRow(sized[0]!, [])];
  }

  const rows = uniqueGeneric.map((entry, index) => ({ ...toRow(entry, []), key: `${kind}-any-${index}` }));
  let run: Array<Entry> = [];
  const flush = () => {
    if (run.length > 0)
      rows.push(
        toRow(
          run[0]!,
          run.map((entry) => entry.size as PetSize),
        ),
      );
    run = [];
  };
  for (const entry of sized) {
    if (run.length > 0 && run[0]!.signature !== entry.signature) flush();
    run.push(entry);
  }
  flush();
  return rows.map((row, index) => ({ ...row, key: `${row.key}-${index}` }));
}

export function buildPetRows(map: PetPolicyMap | null | undefined): Array<PetRow> {
  if (!map) return [];
  return PET_KINDS.flatMap((kind) => {
    const policies = map[kind];
    return policies && policies.length > 0 ? rowsForKind(kind, policies) : [];
  });
}

export type SharedPetNotes = Pick<PetRow, 'restriction' | 'comment'>;

export function hoistSharedNotes(rows: Array<PetRow>): { rows: Array<PetRow>; shared: SharedPetNotes } {
  const sharedValue = (field: keyof SharedPetNotes): string | null => {
    const first = rows[0]?.[field];
    return rows.length > 1 && first && rows.every((row) => row[field] === first) ? first : null;
  };
  const shared = { restriction: sharedValue('restriction'), comment: sharedValue('comment') };
  return {
    shared,
    rows: rows.map((row) => ({
      ...row,
      restriction: shared.restriction ? null : row.restriction,
      comment: shared.comment ? null : row.comment,
    })),
  };
}

const CURRENCY_PREFIX = /^\$\d+(?:,\d{3})*(?:\.\d{2})?/;
const PLAIN_NUMBER = /^\d[\d,]*(?:\.\d+)?$/;

// Like the legacy numberUtils.asCurrency: whole dollars drop the cents, fractional amounts keep them.
export function formatFeeAmount(value: string | number): string {
  const text = String(value).trim();
  const prefixed = text.match(CURRENCY_PREFIX);
  if (prefixed) return prefixed[0];
  if (PLAIN_NUMBER.test(text)) {
    const amount = Number(text.replace(/,/g, ''));
    if (Number.isFinite(amount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    }
  }
  return text;
}

export function formatWeight(value: string | number): string {
  const match = String(value).match(/^\s*(\d+(?:\.\d+)?)/);
  return match ? String(Number(match[1])) : String(value);
}

export function formatCount(value: string | number): string {
  const match = String(value).match(/^\s*(\d+)/);
  return match ? match[1]! : String(value);
}
